import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { ConsolidatedLocationView, DispatchAreaObject } from '@mark43/cad-api';
import { Divider, Flex, HStack, VStack, Box, IconButton, Text, DataList, DataItem } from '@cad-arc';
import { isValidLatLng } from '../../../location/helpers';
import { eventNotificationPriorityToColors } from '../../../event-notifications/event-notifications';
import { useEsriMapView } from '../../hooks/use-esri-map-view';
import { EntityAddress } from './entity-address';
import { useEffect, useState } from 'react';
import { useLocationApi } from '../../../location-select/hooks/use-location-api';
import { getClosestLocation } from '../../../common/helpers/get-closest-location';
import { usePrevious } from '../../../common/hooks/use-previous';
import { CommonSize, getOneSizeSmaller } from '../../../common/helpers/utils';
import { getDataItemText } from '../../../common/helpers/component-utils';
import { LocationDegreesDataItem } from './location-degrees-data-item';
import { colors } from '../../../colors/colors';

type LocationAddressAndGpsSectionProps = {
    isGpsLocation?: boolean;
    isUserLocation?: boolean;
    shouldSearchReverseGeocodeLocation?: boolean;
    placeName?: string;
    displayLineFirst?: string;
    displayLineSubPremises?: string;
    displayLineOptionalMiddle?: string;
    displayLineLast?: string;
    geoLocationLatitude?: number;
    geoLocationLongitude?: number;
    geoLocationTimestamp?: number;
    areGeoLocationCoordinatesVerified?: boolean;
    source?: string;
    gpsLocationLatitude?: number;
    gpsLocationLongitude?: number;
    gpsLocationTimestamp?: number;
    areGpsLocationCoordinatesVerified?: boolean;
    confidenceValue?: string;
    stationCrossStreet1?: string;
    stationCrossStreet2?: string;
    stationDispatchArea?: DispatchAreaObject;
    stationTypeDisplay?: string;
    stationFullAddress?: string;
    eventHasLocationNotification?: boolean;
    removeEventNotification?: () => void;
    amlCallHasGpsNotification?: boolean;
    removeAmlCallNotification?: () => void;
    clearSelectedNearbyGeoCautionCoordinates?: () => void;
    locationSectionSubTitle?: string;
    locationSectionTitle?: string;
    showSectionDivider?: boolean;
    size?: CommonSize;
};

export function LocationAddressAndGpsSection({
    isGpsLocation,
    isUserLocation,
    shouldSearchReverseGeocodeLocation,
    placeName,
    displayLineFirst,
    displayLineSubPremises,
    displayLineOptionalMiddle,
    displayLineLast,
    geoLocationLatitude,
    geoLocationLongitude,
    geoLocationTimestamp,
    areGeoLocationCoordinatesVerified,
    source,
    gpsLocationLatitude,
    gpsLocationLongitude,
    gpsLocationTimestamp,
    areGpsLocationCoordinatesVerified,
    confidenceValue,
    stationCrossStreet1,
    stationCrossStreet2,
    stationDispatchArea,
    stationTypeDisplay,
    stationFullAddress,
    eventHasLocationNotification,
    removeEventNotification,
    amlCallHasGpsNotification,
    removeAmlCallNotification,
    clearSelectedNearbyGeoCautionCoordinates,
    locationSectionSubTitle,
    locationSectionTitle = 'Location',
    showSectionDivider = true,
    size,
}: LocationAddressAndGpsSectionProps): React.ReactElement {
    const { subpremiseSupport } = useEnabledFeatures();
    const { searchLocations, isLoadingResults } = useLocationApi();
    const [
        closestReverseGeocodeLocation,
        setClosestReverseGeocodeLocation,
    ] = useState<ConsolidatedLocationView>();
    const prevGpsLatitude = usePrevious<number | undefined>(gpsLocationLatitude);
    const prevGpsLongitude = usePrevious<number | undefined>(gpsLocationLongitude);

    const { goTo } = useEsriMapView();

    const sectionTitleColor =
        eventHasLocationNotification || amlCallHasGpsNotification
            ? eventNotificationPriorityToColors['STANDARD'].notchOrText
            : 'var(--arc-colors-text-primary)';

    // geoLocation
    const areGeoLocationCoordinatesValid =
        areGeoLocationCoordinatesVerified ||
        isValidLatLng(geoLocationLatitude, geoLocationLongitude);

    const areGpsLocationCoordinatesValid =
        areGpsLocationCoordinatesVerified ||
        isValidLatLng(gpsLocationLatitude, gpsLocationLongitude);

    const stationData = stationCrossStreet1 || stationCrossStreet2 || stationDispatchArea;

    const handleRemoveNotificationClick = () => {
        if (eventHasLocationNotification && removeEventNotification) {
            removeEventNotification();
        }
        if (amlCallHasGpsNotification && removeAmlCallNotification) {
            removeAmlCallNotification();
        }
    };

    const handleLocationButtonClick = () => {
        if (isGpsLocation && areGpsLocationCoordinatesValid) {
            goTo({ latitude: gpsLocationLatitude!, longitude: gpsLocationLongitude! });
        } else if (!isGpsLocation && areGeoLocationCoordinatesValid) {
            goTo({ latitude: geoLocationLatitude!, longitude: geoLocationLongitude! });
        }
        if (clearSelectedNearbyGeoCautionCoordinates) {
            clearSelectedNearbyGeoCautionCoordinates();
        }
    };

    const smallerSize = getOneSizeSmaller(size);

    useEffect(
        function searchReverseGeocodeLocation() {
            if (
                shouldSearchReverseGeocodeLocation &&
                (prevGpsLatitude !== gpsLocationLatitude ||
                    prevGpsLongitude !== gpsLocationLongitude)
            ) {
                setClosestReverseGeocodeLocation(undefined);
                const queryString = areGpsLocationCoordinatesValid
                    ? `${gpsLocationLatitude},${gpsLocationLongitude}`
                    : undefined;
                if (queryString) {
                    searchLocations(queryString)
                        .then((results) => {
                            const closestLocation = getClosestLocation(results);
                            if (closestLocation) {
                                setClosestReverseGeocodeLocation(closestLocation);
                            }
                        })
                        .catch(() => {
                            setClosestReverseGeocodeLocation(undefined);
                        });
                } else {
                    setClosestReverseGeocodeLocation(undefined);
                }
            }
        },
        [
            shouldSearchReverseGeocodeLocation,
            setClosestReverseGeocodeLocation,
            searchLocations,
            areGpsLocationCoordinatesValid,
            prevGpsLatitude,
            gpsLocationLatitude,
            prevGpsLongitude,
            gpsLocationLongitude,
        ]
    );

    return (
        <Box data-testid="MAP_LOCATION_ADDRESS_AND_GPS_SECTION">
            <Flex padding="12px 18px" gap={2}>
                {(eventHasLocationNotification || amlCallHasGpsNotification) && (
                    <Box
                        w="8px"
                        h="auto"
                        bg={colors.eventNotificationsFieldIndicatorBackground}
                        borderRadius="var(--arc-radii-md) 0px 0px var(--arc-radii-md)"
                        cursor="pointer"
                        data-testid="MAP_LOCATION_ADDRESS_AND_GPS_SECTION_LEFT_BORDER_BLOCK"
                        onClick={handleRemoveNotificationClick}
                    />
                )}
                <VStack spacing={3} width="100%">
                    <Flex alignItems="center" justifyContent="space-between" width="100%">
                        <Text
                            style={{ color: sectionTitleColor }}
                            fontSize={size}
                            fontWeight="bold"
                            data-testid="MAP_LOCATION_ADDRESS_AND_GPS_SECTION_TITLE"
                        >
                            {locationSectionTitle}
                        </Text>
                        <IconButton
                            aria-label="Location"
                            icon="Crosshairs"
                            variant="ghost"
                            size={size}
                            disabled={
                                isGpsLocation
                                    ? !areGpsLocationCoordinatesValid
                                    : !areGeoLocationCoordinatesValid
                            }
                            onClick={handleLocationButtonClick}
                            data-testid="MAP_CROSSHAIRS_BUTTON"
                        />
                    </Flex>
                    {locationSectionSubTitle && (
                        <Box width="100%">
                            <Text fontSize={size}>{locationSectionSubTitle}</Text>
                        </Box>
                    )}
                    {shouldSearchReverseGeocodeLocation ? (
                        <EntityAddress
                            isGpsLocation={isGpsLocation}
                            shouldSearchReverseGeocodeLocation={true}
                            addressMessage={
                                isLoadingResults
                                    ? 'Loading...'
                                    : !isLoadingResults && !closestReverseGeocodeLocation
                                    ? 'No data found'
                                    : undefined
                            }
                            source={closestReverseGeocodeLocation?.source}
                            placeName={closestReverseGeocodeLocation?.placeName}
                            areGeoLocationCoordinatesVerified={
                                closestReverseGeocodeLocation?.areCoordinatesVerified
                            }
                            displayLineFirst={closestReverseGeocodeLocation?.displayLineFirst}
                            displayLineLast={closestReverseGeocodeLocation?.displayLineLast}
                            displayLineOptionalMiddle={
                                closestReverseGeocodeLocation?.displayLineOptionalMiddle
                            }
                            subpremiseSupport={subpremiseSupport}
                            displayLineSubPremises={
                                closestReverseGeocodeLocation?.displayLineSubPremises
                            }
                            locationTimestamp={isGpsLocation ? undefined : geoLocationTimestamp}
                            size={size}
                        />
                    ) : (
                        <EntityAddress
                            isGpsLocation={isGpsLocation}
                            shouldSearchReverseGeocodeLocation={false}
                            source={source}
                            placeName={placeName}
                            areGeoLocationCoordinatesVerified={areGeoLocationCoordinatesValid}
                            areGpsLocationCoordinatesVerified={areGpsLocationCoordinatesVerified}
                            displayLineFirst={displayLineFirst}
                            displayLineLast={displayLineLast}
                            displayLineOptionalMiddle={displayLineOptionalMiddle}
                            subpremiseSupport={subpremiseSupport}
                            displayLineSubPremises={displayLineSubPremises}
                            locationTimestamp={isGpsLocation ? undefined : geoLocationTimestamp}
                            stationFullAddress={stationFullAddress}
                            size={size}
                        />
                    )}
                    <DataList
                        direction="column"
                        variant="stacked"
                        spacing={3}
                        style={{ width: '100%' }}
                    >
                        <LocationDegreesDataItem
                            size={smallerSize}
                            isUserLocation={isUserLocation}
                            isGpsLocation={isGpsLocation}
                            isGpsLocationValid={areGpsLocationCoordinatesValid}
                            isGeoLocationValid={areGeoLocationCoordinatesValid}
                            gpsLocationLatitude={gpsLocationLatitude}
                            gpsLocationLongitude={gpsLocationLongitude}
                            gpsLocationTimestamp={gpsLocationTimestamp}
                            geoLocationLatitude={geoLocationLatitude}
                            geoLocationLongitude={geoLocationLongitude}
                        />
                        {confidenceValue && (
                            <DataItem
                                dataKey={
                                    <Text fontSize={smallerSize} fontWeight="bold">
                                        Confidence
                                    </Text>
                                }
                                style={{ wordBreak: 'break-word' }}
                            >
                                {getDataItemText(size, confidenceValue)}
                            </DataItem>
                        )}
                        {stationData && (
                            <HStack alignItems="flex-start" gap={4}>
                                <DataItem
                                    dataKey={
                                        <Text fontSize={smallerSize} fontWeight="bold">
                                            Cross
                                        </Text>
                                    }
                                    style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                                >
                                    <Text fontSize={size}>
                                        {stationCrossStreet1 || '--'} /{' '}
                                        {stationCrossStreet2 || '--'}
                                    </Text>
                                </DataItem>

                                <DataItem
                                    dataKey={
                                        <Text fontSize={smallerSize} fontWeight="bold">
                                            {stationTypeDisplay} D-Area
                                        </Text>
                                    }
                                    style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                                >
                                    {getDataItemText(size, stationDispatchArea?.name)}
                                </DataItem>
                            </HStack>
                        )}
                    </DataList>
                </VStack>
            </Flex>
            {showSectionDivider && <Divider />}
        </Box>
    );
}
